import { create } from 'zustand';
import { api } from './api';
import type {
  AiAction,
  AiAnalysis,
  AuditEntry,
  BoardStyle,
  CollectBundle,
  Engagement,
  Entity,
  Evidence,
  Hypothesis,
  AiModelList,
  AiProviderInfo,
  MetaConfig,
  OperatePlan,
  Requirement,
  RequirementCoverage,
  Relation,
  Stats,
  Target,
  TimelineEvent,
} from './domain';

export type Toast = { id: string; kind: 'info' | 'success' | 'error'; message: string };

type StoreState = {
  booted: boolean;
  loading: boolean;
  error: string | null;
  meta: MetaConfig | null;

  engagements: Engagement[];
  activeEngagementId: string | null;

  targets: Target[];
  activeTargetId: string | null;

  entities: Entity[];
  relations: Relation[];
  evidence: Evidence[];
  hypotheses: Hypothesis[];
  timeline: TimelineEvent[];
  requirements: Requirement[];
  requirementCoverage: RequirementCoverage | null;
  stats: Stats | null;
  audit: AuditEntry[];

  boardNodes: { id: string; type: string; refKind: string | null; refId: string | null; payload: Record<string, unknown>; z: string }[];
  boardViewport: { x: number; y: number; zoom: number };
  boardStyle: BoardStyle;
  /** 已为哪个案件加载完画布数据（用于驱动画布初始化）。 */
  boardLoadedFor: string | null;
  /** 左右侧栏展开状态（关闭后关联墙占满窗口）。 */
  panels: { left: boolean; right: boolean };
  /** 右侧栏当前页（研判/档案/元素）。 */
  rightTab: 'dossier' | 'assess' | 'graph' | 'ach' | 'geo' | 'operate' | 'element';
  /** 全屏预览模式：隐藏全部浮层与侧栏，只留墙面。 */
  preview: boolean;
  /** 请求启动自动巡航（配合全屏预览）。 */
  pendingTour: boolean;
  /** 只读共享演示视图：隐藏编辑能力与私密数据。 */
  readOnlyView: boolean;
  /** 情报简报（打印 / 另存 PDF）浮层。 */
  briefOpen: boolean;
  /** 关联墙自动保存状态（用于界面提示）。 */
  boardSave: { state: 'clean' | 'dirty' | 'saving' | 'saved' | 'error'; at: string | null; error?: string };
  /** 各个浮层的显隐开关。 */
  hud: HudVisibility;
  /** 进入预览前的视图状态（用于退出时恢复）。 */
  prePreview: { panels: { left: boolean; right: boolean }; hud: HudVisibility } | null;

  aiAnalysis: AiAnalysis | null;
  aiPlan: OperatePlan | null;
  planningActions: boolean;
  applyingActions: boolean;
  /** 最近一次 AI 应用的回滚记录。 */
  lastApplyBatch: import('./aiOperate').ApplyBatch | null;
  lastBundle: CollectBundle | null;
  toasts: Toast[];

  /** AI 多供应商 / 多模型选择 */
  aiProviders: AiProviderInfo[];
  aiProviderId: string | null;
  aiModel: string;
  aiModels: string[];

  bootstrap: () => Promise<void>;
  toast: (kind: Toast['kind'], message: string) => void;
  dismissToast: (id: string) => void;

  selectEngagement: (id: string) => Promise<void>;
  createEngagement: (body: Partial<Engagement> & { name: string }) => Promise<void>;

  refreshTargets: () => Promise<void>;
  refreshGraph: () => Promise<void>;
  refreshStats: () => Promise<void>;
  refreshAudit: () => Promise<void>;
  selectTarget: (id: string | null) => void;

  createTarget: (body: Record<string, unknown>) => Promise<Target | null>;
  deleteTarget: (id: string) => Promise<void>;

  collect: (kind: string, value: string) => Promise<CollectBundle | null>;
  analyze: (focus?: string) => Promise<AiAnalysis | null>;
  analyzeDraft: (body: Record<string, unknown>) => Promise<AiAnalysis | null>;

  saveBoard: (payload: {
    viewport?: { x: number; y: number; zoom: number };
    style?: BoardStyle;
    upserts?: unknown[];
    deletes?: string[];
  }) => Promise<void>;

  addEntity: (body: Record<string, unknown>) => Promise<void>;
  updateEntity: (id: string, body: Record<string, unknown>) => Promise<void>;
  deleteEntity: (id: string) => Promise<void>;
  importIntel: (body: Record<string, unknown>) => Promise<void>;
  addRelation: (body: Record<string, unknown>) => Promise<void>;
  updateRelation: (id: string, body: Record<string, unknown>) => Promise<void>;
  deleteRelation: (id: string) => Promise<void>;
  addEvidence: (body: Record<string, unknown>) => Promise<void>;
  updateEvidence: (id: string, body: Record<string, unknown>) => Promise<void>;
  deleteEvidence: (id: string) => Promise<void>;
  addHypothesis: (body: Record<string, unknown>) => Promise<void>;
  updateHypothesis: (id: string, body: Record<string, unknown>) => Promise<void>;
  deleteHypothesis: (id: string) => Promise<void>;
  refreshTimeline: () => Promise<void>;
  refreshRequirements: () => Promise<void>;
  addRequirement: (body: Record<string, unknown>) => Promise<void>;
  updateRequirement: (id: string, body: Record<string, unknown>) => Promise<void>;
  deleteRequirement: (id: string) => Promise<void>;
  addTimeline: (body: Record<string, unknown>) => Promise<void>;
  updateTimeline: (id: string, body: Record<string, unknown>) => Promise<void>;
  deleteTimeline: (id: string) => Promise<void>;
  refreshAiProviders: () => Promise<void>;
  setAiProvider: (providerId: string) => Promise<void>;
  setAiModel: (model: string) => void;
  setBoardStyle: (style: BoardStyle) => void;
  togglePanel: (side: 'left' | 'right') => void;
  setPanels: (panels: { left: boolean; right: boolean }) => void;
  focusWall: () => void;
  setRightTab: (tab: 'dossier' | 'assess' | 'graph' | 'ach' | 'geo' | 'operate' | 'element') => void;
  /** 对指定对象执行研判（不传则用当前对象）。 */
  analyzeTarget: (targetId?: string, focus?: string) => Promise<AiAnalysis | null>;
  /** 规划可执行动作（不落库）。 */
  planActions: (instruction?: string) => Promise<OperatePlan | null>;
  /** 应用选中的动作。 */
  applyActions: (ids: string[]) => Promise<void>;
  /** 回滚最近一次应用。 */
  rollbackLastApply: () => Promise<void>;
  discardPlan: () => void;
  setPreview: (value: boolean) => void;
  /** 一键：进入全屏预览并开始自动巡航。 */
  requestTour: () => void;
  setReadOnlyView: (value: boolean) => void;
  setBriefOpen: (value: boolean) => void;
  setBoardSave: (patch: Partial<{ state: 'clean' | 'dirty' | 'saving' | 'saved' | 'error'; at: string | null; error?: string }>) => void;
  toggleHud: (key: keyof HudVisibility) => void;
  setAllHud: (value: boolean) => void;
};

export type HudVisibility = {
  tools: boolean;
  controls: boolean;
  casePlate: boolean;
  suggestions: boolean;
  minimap: boolean;
  bottomBar: boolean;
};

const emptyStats: Stats = { targets: 0, entities: 0, evidence: 0, relations: 0, hypotheses: 0, byType: [] };

let toastSeq = 0;

export const useStore = create<StoreState>((set, get) => ({
  booted: false,
  loading: false,
  error: null,
  meta: null,
  engagements: [],
  activeEngagementId: null,
  targets: [],
  activeTargetId: null,
  entities: [],
  relations: [],
  evidence: [],
  hypotheses: [],
  timeline: [],
  requirements: [],
  requirementCoverage: null,
  stats: null,
  audit: [],
  boardNodes: [],
  boardViewport: { x: -200, y: -200, zoom: 1 },
  boardStyle: 'paper',
  boardLoadedFor: null,
  panels: { left: true, right: true },
  rightTab: 'dossier',
  preview: false,
  pendingTour: false,
  readOnlyView: false,
  briefOpen: false,
  boardSave: { state: 'clean', at: null },
  prePreview: null,
  hud: {
    tools: true,
    controls: true,
    casePlate: true,
    suggestions: true,
    minimap: true,
    bottomBar: true,
  },
  aiAnalysis: null,
  aiPlan: null,
  planningActions: false,
  applyingActions: false,
  lastApplyBatch: null,
  lastBundle: null,
  toasts: [],
  aiProviders: [],
  aiProviderId: (typeof localStorage !== 'undefined' && localStorage.getItem('expotar.aiProvider')) || null,
  aiModel: (typeof localStorage !== 'undefined' && localStorage.getItem('expotar.aiModel')) || '',
  aiModels: [],

  toast(kind, message) {
    toastSeq += 1;
    const id = `t${toastSeq}`;
    set(state => ({ toasts: [...state.toasts, { id, kind, message }] }));
    setTimeout(() => get().dismissToast(id), kind === 'error' ? 8000 : 4000);
  },

  dismissToast(id) {
    set(state => ({ toasts: state.toasts.filter(t => t.id !== id) }));
  },

  async bootstrap() {
    set({ loading: true, error: null });
    try {
      const [meta, engagements] = await Promise.all([api.config(), api.listEngagements()]);
      set({ meta, engagements: engagements.items, booted: true });
      void get().refreshAiProviders();
      const first = get().activeEngagementId ?? engagements.items[0]?.id ?? null;
      if (first) await get().selectEngagement(first);
    } catch (error) {
      set({ error: (error as Error).message });
      get().toast('error', `初始化失败：${(error as Error).message}`);
    } finally {
      set({ loading: false });
    }
  },

  async selectEngagement(id) {
    set({
      activeEngagementId: id,
      activeTargetId: null,
      aiAnalysis: null,
      boardLoadedFor: null,
      boardNodes: [],
    });
    await Promise.all([
      get().refreshTargets(),
      get().refreshGraph(),
      get().refreshTimeline(),
      get().refreshRequirements(),
      get().refreshStats(),
      get().refreshAudit(),
    ]);
    try {
      const board = await api.getBoard(id);
      set({
        boardNodes: board.nodes,
        boardViewport: board.viewport,
        boardStyle: board.style ?? 'paper',
        boardLoadedFor: id,
      });
    } catch {
      set({
        boardNodes: [],
        boardViewport: { x: -200, y: -200, zoom: 1 },
        boardStyle: 'paper',
        boardLoadedFor: id,
      });
    }
  },

  async createEngagement(body) {
    try {
      const created = await api.createEngagement(body);
      set(state => ({ engagements: [created, ...state.engagements] }));
      get().toast('success', '授权委托已创建');
      await get().selectEngagement(created.id);
    } catch (error) {
      get().toast('error', (error as Error).message);
    }
  },

  async refreshTargets() {
    const id = get().activeEngagementId;
    if (!id) return;
    try {
      const { items } = await api.listTargets(id);
      set({ targets: items });
    } catch (error) {
      get().toast('error', (error as Error).message);
    }
  },

  async refreshGraph() {
    const id = get().activeEngagementId;
    if (!id) return;
    try {
      const [entities, relations, evidence, hypotheses] = await Promise.all([
        api.listEntities(id),
        api.listRelations(id),
        api.listEvidence(id),
        api.listHypotheses(id),
      ]);
      set({
        entities: entities.items,
        relations: relations.items,
        evidence: evidence.items,
        hypotheses: hypotheses.items,
      });
    } catch (error) {
      get().toast('error', (error as Error).message);
    }
  },

  async refreshTimeline() {
    const id = get().activeEngagementId;
    if (!id) return;
    try {
      const { items } = await api.listTimeline(id);
      set({ timeline: items });
    } catch {
      set({ timeline: [] });
    }
  },

  async refreshRequirements() {
    const id = get().activeEngagementId;
    if (!id) return;
    try {
      const [list, coverage] = await Promise.all([
        api.listRequirements(id),
        api.requirementCoverage(id),
      ]);
      set({ requirements: list.items, requirementCoverage: coverage });
    } catch {
      set({ requirements: [], requirementCoverage: null });
    }
  },

  async addRequirement(body) {
    const engagementId = get().activeEngagementId;
    if (!engagementId) return;
    try {
      await api.createRequirement({ ...body, engagementId });
      await get().refreshRequirements();
      get().toast('success', '情报需求已登记');
    } catch (error) {
      get().toast('error', (error as Error).message);
    }
  },

  async updateRequirement(id, body) {
    try {
      await api.updateRequirement(id, body);
      await get().refreshRequirements();
    } catch (error) {
      get().toast('error', (error as Error).message);
    }
  },

  async deleteRequirement(id) {
    await api.deleteRequirement(id).catch(() => undefined);
    await get().refreshRequirements();
  },

  async refreshStats() {
    const id = get().activeEngagementId;
    if (!id) return;
    try {
      set({ stats: await api.stats(id) });
    } catch {
      set({ stats: emptyStats });
    }
  },

  async refreshAudit() {
    const id = get().activeEngagementId;
    if (!id) return;
    try {
      const { items } = await api.listAudit(id);
      set({ audit: items });
    } catch {
      /* ignore */
    }
  },

  selectTarget(id) {
    set({ activeTargetId: id, aiAnalysis: null });
  },

  async createTarget(body) {
    const engagementId = get().activeEngagementId;
    if (!engagementId) return null;
    try {
      const created = await api.createTarget({ ...body, engagementId });
      await get().refreshTargets();
      await get().refreshStats();
      get().toast('success', `目标「${created.name}」已创建`);
      set({ activeTargetId: created.id });
      return created;
    } catch (error) {
      get().toast('error', (error as Error).message);
      return null;
    }
  },

  async deleteTarget(id) {
    try {
      await api.deleteTarget(id);
      if (get().activeTargetId === id) set({ activeTargetId: null });
      await get().refreshTargets();
      await get().refreshStats();
      get().toast('success', '目标已删除');
    } catch (error) {
      get().toast('error', (error as Error).message);
    }
  },

  async collect(kind, value) {
    const engagementId = get().activeEngagementId;
    if (!engagementId) return null;
    try {
      get().toast('info', `正在采集 ${value} …`);
      const result = await api.collect({
        engagementId,
        targetId: get().activeTargetId,
        kind,
        value,
        save: true,
      });
      set({ lastBundle: result.bundle });
      await get().refreshGraph();
      await get().refreshTargets();
      await get().refreshStats();
      await get().refreshAudit();
      get().toast('success', result.bundle.summary);
      return result.bundle;
    } catch (error) {
      get().toast('error', (error as Error).message);
      return null;
    }
  },

  async analyze(focus) {
    const engagementId = get().activeEngagementId;
    const targetId = get().activeTargetId;
    if (!engagementId || !targetId) {
      get().toast('error', '请先选择一个目标');
      return null;
    }
    try {
      get().toast('info', 'AI 正在分析 …');
      const result = await api.analyze({
        engagementId,
        targetId,
        focus,
        providerId: get().aiProviderId,
        model: get().aiModel || null,
      });
      set({ aiAnalysis: result.analysis });
      await get().refreshTargets();
      await get().refreshGraph();
      await get().refreshAudit();
      get().toast(
        'success',
        result.analysis.mode === 'remote' ? 'AI 分析完成' : '本地启发式分析完成'
      );
      return result.analysis;
    } catch (error) {
      get().toast('error', (error as Error).message);
      return null;
    }
  },

  async analyzeDraft(body) {
    const engagementId = get().activeEngagementId;
    if (!engagementId) return null;
    try {
      const result = await api.analyzeDraft({ ...body, engagementId });
      set({ aiAnalysis: result.analysis });
      return result.analysis;
    } catch (error) {
      get().toast('error', (error as Error).message);
      return null;
    }
  },

  async saveBoard(payload) {
    const engagementId = get().activeEngagementId;
    if (!engagementId) return;
    set({ boardSave: { ...get().boardSave, state: 'saving' } });
    try {
      await api.syncBoard({ engagementId, ...payload });
      set({ boardSave: { state: 'saved', at: new Date().toISOString() } });
    } catch (error) {
      set({ boardSave: { state: 'error', at: null, error: (error as Error).message } });
      get().toast('error', `画布保存失败：${(error as Error).message}`);
    }
  },
  async addEntity(body) {
    const engagementId = get().activeEngagementId;
    if (!engagementId) return;
    try {
      await api.createEntity({ ...body, engagementId });
      await get().refreshGraph();
      await get().refreshStats();
    } catch (error) {
      get().toast('error', (error as Error).message);
    }
  },

  async updateEntity(id, body) {
    try {
      await api.updateEntity(id, body);
      await get().refreshGraph();
      await get().refreshTargets();
      await get().refreshStats();
    } catch (error) {
      get().toast('error', (error as Error).message);
    }
  },

  async updateRelation(id, body) {
    try {
      await api.updateRelation(id, body);
      await get().refreshGraph();
    } catch (error) {
      get().toast('error', (error as Error).message);
    }
  },

  async updateEvidence(id, body) {
    try {
      await api.updateEvidence(id, body);
      await get().refreshGraph();
      await get().refreshTargets();
    } catch (error) {
      get().toast('error', (error as Error).message);
    }
  },

  async addTimeline(body) {
    const engagementId = get().activeEngagementId;
    if (!engagementId) return;
    try {
      await api.createTimeline({ ...body, engagementId });
      await get().refreshTimeline();
      await get().refreshStats();
      get().toast('success', '事件已记入时间线');
    } catch (error) {
      get().toast('error', (error as Error).message);
    }
  },

  async updateTimeline(id, body) {
    try {
      await api.updateTimeline(id, body);
      await get().refreshTimeline();
    } catch (error) {
      get().toast('error', (error as Error).message);
    }
  },

  async deleteTimeline(id) {
    await api.deleteTimeline(id).catch(() => undefined);
    await get().refreshTimeline();
    await get().refreshStats();
  },

  async refreshAiProviders() {
    try {
      const status = await api.aiStatus();
      const enabled = status.providers.filter(p => p.enabled);
      set({ aiProviders: status.providers });
      const current = get().aiProviderId;
      const nextId =
        (current && enabled.some(p => p.id === current) ? current : null) ??
        status.defaultProviderId ??
        enabled[0]?.id ??
        null;
      if (nextId && nextId !== current) {
        set({ aiProviderId: nextId });
        if (typeof localStorage !== 'undefined') localStorage.setItem('expotar.aiProvider', nextId);
      }
      if (nextId) {
        const provider = enabled.find(p => p.id === nextId);
        const models = provider?.models ?? [];
        const model = get().aiModel;
        set({
          aiModels: models,
          aiModel: model && models.includes(model) ? model : provider?.defaultModel ?? models[0] ?? '',
        });
      }
    } catch {
      set({ aiProviders: [] });
    }
  },

  async setAiProvider(providerId) {
    set({ aiProviderId: providerId });
    if (typeof localStorage !== 'undefined') localStorage.setItem('expotar.aiProvider', providerId);
    const provider = get().aiProviders.find(p => p.id === providerId);
    set({ aiModels: provider?.models ?? [], aiModel: provider?.defaultModel ?? '' });
    if (typeof localStorage !== 'undefined' && provider?.defaultModel) {
      localStorage.setItem('expotar.aiModel', provider.defaultModel);
    }
    try {
      const list: AiModelList = await api.aiModels(providerId);
      if (list.models.length) {
        set({
          aiModels: list.models,
          aiModel: list.models.includes(get().aiModel) ? get().aiModel : list.defaultModel || list.models[0],
        });
        if (list.source === 'remote') {
          get().toast('info', `已从「${list.providerName}」拉取 ${list.models.length} 个模型`);
        } else if (list.error) {
          get().toast('info', `无法拉取远端模型清单，使用配置值`);
        }
      }
    } catch (error) {
      get().toast('error', (error as Error).message);
    }
  },

  setAiModel(model) {
    set({ aiModel: model });
    if (typeof localStorage !== 'undefined') localStorage.setItem('expotar.aiModel', model);
  },

  setBoardStyle(style) {
    set({ boardStyle: style });
    void get().saveBoard({ style });
  },

  togglePanel(side) {
    set(state => ({ panels: { ...state.panels, [side]: !state.panels[side] } }));
  },

  setPanels(panels) {
    set({ panels });
  },

  /** 释放关联墙：收起两侧栏。 */
  focusWall() {
    const { panels } = get();
    if (panels.left && panels.right) set({ panels: { left: false, right: false } });
    else set({ panels: { left: true, right: true } });
  },

  setRightTab(tab) {
    set({ rightTab: tab });
  },

  async analyzeTarget(targetId, focus) {
    const id = targetId ?? get().activeTargetId ?? get().targets[0]?.id;
    if (!id) {
      get().toast('error', '当前案件没有对象，无法研判');
      return null;
    }
    set({ activeTargetId: id, rightTab: 'assess', panels: { ...get().panels, right: true } });
    return get().analyze(focus);
  },

  async planActions(instruction) {
    const engagementId = get().activeEngagementId;
    if (!engagementId) return null;
    set({ planningActions: true, rightTab: 'operate', panels: { ...get().panels, right: true } });
    try {
      const plan = await api.operate({
        engagementId,
        targetId: get().activeTargetId,
        instruction,
        providerId: get().aiProviderId,
        model: get().aiModel || null,
      });
      set({ aiPlan: plan });
      await get().refreshAudit();
      get().toast(
        plan.actions.length ? 'success' : 'info',
        plan.providerError ? `已回退本地规划：${plan.providerError}` : plan.summary
      );
      return plan;
    } catch (error) {
      get().toast('error', (error as Error).message);
      return null;
    } finally {
      set({ planningActions: false });
    }
  },

  async applyActions(ids) {
    const plan = get().aiPlan;
    if (!plan) return;
    const selected = plan.actions.filter(action => ids.includes(action.id));
    if (selected.length === 0) {
      get().toast('error', '未选择任何动作');
      return;
    }
    set({ applyingActions: true });
    try {
      const { applyAiActions } = await import('./aiOperate');
      const controller = (await import('../canvas/canvasStore')).useCanvasStore.getState().controller;
      const result = await applyAiActions(selected as AiAction[], controller);
      const remaining = plan.actions.filter(action => !ids.includes(action.id));
      set({
        aiPlan: remaining.length ? { ...plan, actions: remaining } : null,
        lastApplyBatch: result.batch,
      });
      await get().refreshAudit();
      get().toast(
        result.applied ? 'success' : 'info',
        `已应用 ${result.applied} 项，跳过 ${result.skipped} 项${result.batch ? '（可撤销）' : ''}`
      );
      if (result.messages.length) {
        for (const message of result.messages.slice(0, 3)) get().toast('info', message);
      }
    } catch (error) {
      get().toast('error', (error as Error).message);
    } finally {
      set({ applyingActions: false });
    }
  },

  async rollbackLastApply() {
    const batch = get().lastApplyBatch;
    if (!batch) {
      get().toast('info', '没有可撤销的应用记录');
      return;
    }
    try {
      const { rollbackApplyBatch } = await import('./aiOperate');
      const controller = (await import('../canvas/canvasStore')).useCanvasStore.getState().controller;
      const { removed, restored } = await rollbackApplyBatch(batch, controller);
      set({ lastApplyBatch: null });
      await get().refreshAudit();
      get().toast('success', `已撤销：删除 ${removed} 项，恢复 ${restored} 项`);
    } catch (error) {
      get().toast('error', (error as Error).message);
    }
  },

  discardPlan() {
    set({ aiPlan: null });
  },

  setPreview(value) {
    set(state => {
      if (value) {
        return {
          preview: true,
          prePreview: { panels: state.panels, hud: state.hud },
          panels: { left: false, right: false },
          hud: {
            tools: false,
            controls: false,
            casePlate: false,
            suggestions: false,
            minimap: false,
            bottomBar: false,
          },
        };
      }
      const restore = state.prePreview;
      return {
        preview: false,
        prePreview: null,
        panels: restore?.panels ?? { left: true, right: true },
        hud: restore?.hud ?? {
          tools: true,
          controls: true,
          casePlate: true,
          suggestions: true,
          minimap: true,
          bottomBar: true,
        },
      };
    });
  },

  toggleHud(key) {
    set(state => ({ hud: { ...state.hud, [key]: !state.hud[key] } }));
  },

  setAllHud(value) {
    set({
      hud: {
        tools: value,
        controls: value,
        casePlate: value,
        suggestions: value,
        minimap: value,
        bottomBar: value,
      },
    });
  },

  requestTour() {
    set({ pendingTour: true });
    get().setPreview(true);
  },

  setBriefOpen(value) {
    set({ briefOpen: value });
  },

  setBoardSave(patch) {
    set(state => ({ boardSave: { ...state.boardSave, ...patch } }));
  },

  setReadOnlyView(value) {
    set({
      readOnlyView: value,
      panels: { left: false, right: false },
      hud: {
        tools: false,
        controls: false,
        casePlate: value,
        suggestions: false,
        minimap: true,
        bottomBar: value,
      },
    });
  },

  async importIntel(body) {
    const engagementId = get().activeEngagementId;
    if (!engagementId) return;
    try {
      const result = await api.importIntel({ ...body, engagementId });
      await get().refreshGraph();
      await get().refreshStats();
      get().toast('success', `已导入 ${result.created} 个实体、${result.relations} 条关系`);
    } catch (error) {
      get().toast('error', (error as Error).message);
    }
  },

  async deleteEntity(id) {
    await api.deleteEntity(id).catch(() => undefined);
    await get().refreshGraph();
    await get().refreshStats();
  },

  async addRelation(body) {
    const engagementId = get().activeEngagementId;
    if (!engagementId) return;
    try {
      await api.createRelation({ ...body, engagementId });
      await get().refreshGraph();
      await get().refreshStats();
    } catch (error) {
      get().toast('error', (error as Error).message);
    }
  },

  async deleteRelation(id) {
    await api.deleteRelation(id).catch(() => undefined);
    await get().refreshGraph();
    await get().refreshStats();
  },

  async addEvidence(body) {
    const engagementId = get().activeEngagementId;
    if (!engagementId) return;
    try {
      await api.createEvidence({ ...body, engagementId });
      await get().refreshGraph();
      await get().refreshStats();
    } catch (error) {
      get().toast('error', (error as Error).message);
    }
  },

  async deleteEvidence(id) {
    await api.deleteEvidence(id).catch(() => undefined);
    await get().refreshGraph();
    await get().refreshStats();
  },

  async addHypothesis(body) {
    const engagementId = get().activeEngagementId;
    if (!engagementId) return;
    try {
      await api.createHypothesis({ ...body, engagementId });
      await get().refreshGraph();
      await get().refreshStats();
    } catch (error) {
      get().toast('error', (error as Error).message);
    }
  },

  async updateHypothesis(id, body) {
    try {
      await api.updateHypothesis(id, body);
      await get().refreshGraph();
    } catch (error) {
      get().toast('error', (error as Error).message);
    }
  },

  async deleteHypothesis(id) {
    await api.deleteHypothesis(id).catch(() => undefined);
    await get().refreshGraph();
    await get().refreshStats();
  },
}));